/**
 * Tools that pose, show or hide, color and find objects. Each takes one
 * `object`, or several as `objects`: a call with several applies the entries
 * that read cleanly as one step and answers one line per entry, in the order
 * given.
 */

import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import type { TransformNode } from '@babylonjs/core/Meshes/transformNode';

import type { SceneObject } from '../../core/SceneObject';
import { setActive } from '../authoring/objectOps';
import { setMaterialColors, setOpacity, type ColorEntry } from '../authoring/colorOps';
import { setTransforms, type TransformEntry } from '../authoring/transformOps';
import type { Refusal } from '../authoring/types';
import { bundle, rollBack } from '../authoring/steps';
import {
    ObjectRefError, POSITION_ANCHORS, VEC3, defineTool, drawnBounds, fail, worldToLocal, keep, materialIndexError, readBatchEntries, readHexColor, readMate, readTransformArgs, readVec3, type ToolDefinition,
    refusalText, resolveEntry, resolveRef, restAgainst, round, whereText, type PositionAnchor, type ToolInput, type Vec3,
} from './input';
import type { JsonSchema, SceneTool, SceneToolHost, ToolOutcome } from './types';
import { CompositeCommand } from '../undo/CompositeCommand';
import type { UndoCommand } from '../undo/UndoCommand';
import { SEAT_DIRECTIONS, type SeatDirection } from '../../creator-tools/fit/sweep';
import { seatParts, seatText, type SeatRequest } from '../../creator-tools/fit/seat';

const LOOSE_VEC3 = { type: 'array', items: { type: 'number' } } as const;
const MATE: JsonSchema = {
    type: 'object',
    properties: { anchor: { type: 'string' }, to: { type: 'string' }, to_anchor: { type: 'string' }, spin: { type: 'number' } },
    required: ['anchor', 'to', 'to_anchor'],
};

const NAMES = { type: 'array', items: { type: 'string' } } as const;
const SEAT: JsonSchema = {
    anyOf: [{ type: 'boolean' }, { type: 'object', properties: { dir: { type: 'string', enum: [...SEAT_DIRECTIONS] }, on: NAMES, ignore: NAMES } }],
};

/** An entry's `seat`: true, or {dir, on, ignore} with names resolved; else what is wrong with it. */
function readSeat(host: SceneToolHost, object: SceneObject, value: unknown): Omit<SeatRequest, 'object'> | null | string {
    if (value === undefined || value === false) return null;
    if (value === true) return {};
    if (value === null || typeof value !== 'object' || Array.isArray(value)) return 'Error: seat is true or {dir, on, ignore}.';
    const { dir, on, ignore } = value as ToolInput;
    if (dir !== undefined && !SEAT_DIRECTIONS.includes(dir as SeatDirection)) return `Error: seat.dir ${JSON.stringify(dir)} is not one of ${SEAT_DIRECTIONS.join(' ')}.`;
    const objects = (refs: unknown, key: string): SceneObject[] | string | undefined => {
        if (refs === undefined) return undefined;
        const list = Array.isArray(refs) ? refs : [refs];
        let found: (SceneObject | null)[];
        try { found = list.map(ref => resolveRef(host, ref)); }
        catch (error) {
            if (!(error instanceof ObjectRefError)) throw error;
            return `Error: seat.${key}: ${error.message}`;
        }
        const missing = list.filter((_, i) => !found[i] || found[i] === object);
        return missing.length ? `Error: seat.${key}: ${missing.map(r => JSON.stringify(r)).join(', ')} not found, or the part itself.` : found as SceneObject[];
    };
    const onObjects = objects(on, 'on'), ignored = objects(ignore, 'ignore');
    if (typeof onObjects === 'string') return onObjects;
    if (typeof ignored === 'string') return ignored;
    return { ...(dir !== undefined ? { dir: dir as SeatDirection } : {}), ...(onObjects ? { on: onObjects } : {}), ...(ignored ? { ignore: ignored } : {}) };
}

/** A batch's answer: an error when no entry applied, since the call then changed nothing. */
function batchOutcome(text: string, applied: number): ToolOutcome {
    return applied > 0 ? { text } : fail(text);
}

/** Whether a call names several objects (`objects`); an answer when it names one as `object` too. */
function several(input: ToolInput, tool: string): boolean | ToolOutcome {
    const many = input.objects !== undefined;
    return many && input.object !== undefined ? fail(`Error: ${tool} takes \`object\` or \`objects\`, not both.`) : many;
}

const ACTIVE_FIELDS = { object: { type: 'string' }, active: { type: 'boolean' } } as const;
const COLOR_FIELDS = { object: { type: 'string' }, color: { type: 'string' }, material_index: { type: 'number' } } as const;

/** An `objects` item: a name, which takes the call's top-level value, or an entry with its own. */
function entryOr(fields: Record<string, JsonSchema>): JsonSchema {
    return { anyOf: [{ type: 'string' }, { type: 'object', properties: fields, required: ['object'] }] };
}

const POSITION_RELATIVE: JsonSchema = {
    type: 'object',
    properties: { target: { type: 'string' }, anchor: { type: 'string', enum: [...POSITION_ANCHORS] }, offset: { type: 'number' } },
    required: ['target', 'anchor'],
};

const SIZE: JsonSchema = { type: 'array', items: { type: ['number', 'null'] }, minItems: 3, maxItems: 3 };

/** `size`: world metres per axis, null to keep one; else what is wrong. */
function readSize(value: unknown): (number | null)[] | null | string {
    if (value === undefined) return null;
    if (!Array.isArray(value) || value.length !== 3 || !value.every(v => v === null || (typeof v === 'number' && v > 0))) {
        return 'size is [x, y, z] in metres, each above 0, or null to keep that axis.';
    }
    return value.every(v => v === null) ? null : value as (number | null)[];
}

/**
 * Scale `object` so what it draws measures `size` in world metres on each
 * given axis, keeping its bottom where it was when `keepBottom`; a refusal's
 * text, or null. Each world axis is matched to the local axis that points
 * along it, so quarter turns work; an object turned at any other angle is
 * refused, since its world box is not its own size.
 */
async function resizeTo(object: SceneObject, size: (number | null)[], keepBottom: boolean, apply: (entry: TransformEntry) => Promise<string | null>): Promise<string | null> {
    const before = drawnBounds(object);
    if (!before) return `"${object.name}" draws nothing, so it has no size to change. Nothing moved.`;
    const now = [before.max.x - before.min.x, before.max.y - before.min.y, before.max.z - before.min.z];
    object.transform.computeWorldMatrix(true);
    const world = object.transform.getWorldMatrix();
    // The world direction of each local axis, and which world axis it lies along.
    const along = [Vector3.Right(), Vector3.Up(), Vector3.Forward()].map(axis => {
        const d = Vector3.TransformNormal(axis, world).normalize().asArray().map(Math.abs);
        const i = d.findIndex(c => c > 1 - 1e-6);
        return i;
    });
    const s = object.transform.scaling.asArray();
    const scale = [...s] as Vec3;
    for (const [worldAxis, target] of size.entries()) {
        if (target === null) continue;
        const local = along.indexOf(worldAxis);
        if (local < 0) return `"${object.name}" is turned at an angle, so its size along ${'xyz'[worldAxis]} isn't one of its own axes; set scale instead. Nothing moved.`;
        if (now[worldAxis] <= 1e-9) return `"${object.name}" is flat along ${'xyz'[worldAxis]}, so it has no size there to change. Nothing moved.`;
        scale[local] = s[local] * (target / now[worldAxis]);
    }
    const refused = await apply({ object, scale });
    if (refused || !keepBottom) return refused;
    const after = drawnBounds(object);
    if (!after || Math.abs(after.min.y - before.min.y) < 1e-6) return null;
    const lifted = object.transform.getAbsolutePosition().add(new Vector3(0, before.min.y - after.min.y, 0));
    return apply({ object, position: worldToLocal(lifted, object.transform.parent as TransformNode | null) });
}

/** An existing object's `position_relative`: the object to rest against, which face and how far; else what is wrong. */
function readRelative(host: SceneToolHost, object: SceneObject, value: unknown): { target: SceneObject; anchor: PositionAnchor; offset: number } | string {
    if (value === null || typeof value !== 'object' || Array.isArray(value)) return 'position_relative is {target, anchor, offset}.';
    const { target, anchor, offset } = value as ToolInput;
    const on = resolveRef(host, target);
    if (!on) return `position_relative target ${JSON.stringify(target)} not found.`;
    if (on === object) return 'position_relative target is the object itself.';
    if (!POSITION_ANCHORS.includes(anchor as PositionAnchor)) return `position_relative anchor ${JSON.stringify(anchor)} is not one of ${POSITION_ANCHORS.join(', ')}.`;
    if (offset !== undefined && typeof offset !== 'number') return 'position_relative offset is a number of metres.';
    return { target: on, anchor: anchor as PositionAnchor, offset: offset ?? 0 };
}

/** A mate refusal names the anchor or target that is wrong, so nothing moved. */
function mateRefusalText(host: SceneToolHost, refusal: Refusal): string {
    return `${refusalText(host, refusal)} Nothing moved.`;
}

/** One `objects` entry of set_transform: absolute values, or ones relative to the pose before the call, then a seat. */
const TRANSFORM_ENTRY: JsonSchema = {
    type: 'object',
    properties: {
        object: { type: 'string' },
        position: LOOSE_VEC3,
        rotation: LOOSE_VEC3,
        scale: LOOSE_VEC3,
        delta_position: LOOSE_VEC3,
        scale_factor: { type: 'number' },
        mate: MATE,
        seat: SEAT,
    },
    required: ['object'],
};

// Not idempotent: an entry's delta_position or scale_factor moves it again on a second call.
const SET_TRANSFORM: ToolDefinition = {
    name: 'set_transform',
    verb: 'Adjusting',
    edits: true,
    inputSchema: {
        type: 'object',
        properties: {
            object: { type: 'string' }, position: VEC3, position_relative: POSITION_RELATIVE, rotation: VEC3, scale: VEC3, size: SIZE, mate: MATE,
            objects: { type: 'array', items: TRANSFORM_ENTRY },
        },
    },
    async run(input, host) {
        const many = several(input, 'set_transform');
        if (typeof many === 'object') return many;
        if (!many) return transformOne(input, host);
        // Each entry carries its own pose: a top-level one would apply to none of them.
        const shared = ['position', 'position_relative', 'rotation', 'scale', 'size', 'mate'].filter(key => input[key] !== undefined);
        if (shared.length) return fail(`Error: with \`objects\`, give each entry its own ${shared.join(', ')}; top-level ones apply to no entry. Nothing moved.`);
        return transformMany(input.objects, host);
    },
};
const setTransformTool = defineTool(SET_TRANSFORM);

/** set_transform on one object, which it can also size or rest against another: one step. */
async function transformOne(input: ToolInput, host: SceneToolHost): Promise<ToolOutcome> {
    const transform = readTransformArgs(input);
    if ('error' in transform) return fail(transform.error);
    const { position, rotation, scale } = transform;
    const relative = position ? undefined : input.position_relative;
    const size = readSize(input.size);
    if (typeof size === 'string') return fail(`Error: ${size}`);
    if (size && input.mate !== undefined) return fail('Error: size and mate together would size it off the anchor it joins; size it first, then mate it. Nothing moved.');
    if (!position && !rotation && !scale && input.mate === undefined && relative === undefined && !size) {
        return fail('Error: At least one of position, position_relative, rotation, scale, size or mate must be provided.');
    }
    const target = resolveRef(host, input.object);
    if (!target) return fail(`Error: Object "${String(input.object)}" not found.`);
    const mate = input.mate === undefined ? undefined : readMate(input.mate, ref => resolveRef(host, ref));
    if (typeof mate === 'string') return fail(`Error: ${mate} Nothing moved.`);
    const against = relative === undefined ? null : readRelative(host, target, relative);
    if (typeof against === 'string') return fail(`Error: ${against} Nothing moved.`);

    // Turned and scaled first, so the object rests against the target at its new size.
    const steps: UndoCommand[] = [];
    const orphaned: string[] = [];
    const apply = async (entry: TransformEntry): Promise<string | null> => {
        const result = await setTransforms([entry], host.ports());
        const [refused] = result.refused;
        if (refused) return refused.code === 'INVALID_MATE' ? mateRefusalText(host, refused) : refusalText(host, refused);
        if (result.command) steps.push(result.command);
        orphaned.push(...(result.orphaned ?? []));
        return null;
    };
    let refusal: string | null;
    try {
        refusal = position || rotation || scale || mate ? await apply({ object: target, position, rotation, scale, mate }) : null;
        if (!refusal && size) refusal = await resizeTo(target, size, !position && !against, apply);
        if (!refusal && against) {
            const rest = restAgainst(target, against.target, against.anchor, against.offset);
            refusal = rest ? await apply({ object: target, position: rest }) : `"${target.name}" or "${against.target.name}" has no bounds to place by. Nothing moved.`;
        }
    } catch (error) {
        await rollBack(steps, error);
        throw error;
    }
    if (refusal) {
        await rollBack(steps, null);
        return fail(`Error: ${refusal}`);
    }
    keep(host, 'set_transform', { command: steps.length > 1 ? bundle(`Move ${target.name}`, steps) : steps[0] ?? null, orphaned });

    const changes: string[] = [];
    if (position) changes.push(`position=[${position.join(', ')}]`);
    if (rotation) changes.push(`rotation=[${rotation.join(', ')}] degrees`);
    if (scale) changes.push(`scale=[${scale.join(', ')}]`);
    if (mate) {
        const t = target.transform;
        const p = t.position;
        const r = t.rotationQuaternion?.toEulerAngles() ?? t.rotation;
        changes.push(`mate → position=[${[p.x, p.y, p.z].map(n => round(n, 4)).join(', ')}], rotation=[${[r.x, r.y, r.z].map(n => round(n * 180 / Math.PI, 4)).join(', ')}] degrees`);
    }
    if (size) changes.push(`size=[${size.map(v => v ?? 'kept').join(', ')}]`);
    if (against) changes.push(`position ${against.anchor} "${against.target.name}"`);
    const ignored = position && input.position_relative !== undefined ? ' position_relative was ignored: position was given.' : '';
    return { text: `Set ${changes.join(', ')} on "${target.name}".${ignored}${whereText(target)}` };
}

/** set_transform on several objects, applied in order as one step, then seated: one line each. */
async function transformMany(updates: unknown, host: SceneToolHost): Promise<ToolOutcome> {
    const tool = 'set_transform';
    if (!Array.isArray(updates) || updates.length === 0) {
        return fail(`Error: Provide \`objects\` as a non-empty array of {object, position?, rotation?, scale?, delta_position?, scale_factor?, mate?, seat?}.`);
    }
    const lines: string[] = [];
    const queued: { entry: TransformEntry; ref: string; line: number; seat: Omit<SeatRequest, 'object'> | null }[] = [];
    // What earlier updates in this call change on each object: a relative
    // update applies to the pose before the call, which they have changed.
    const changed = new Map<SceneObject, { position: boolean; scale: boolean }>();
    for (const update of updates as unknown[]) {
        const fields = (update !== null && typeof update === 'object' ? update : {}) as ToolInput;
        const ref = String(fields.object);
        const target = resolveEntry(host, fields.object);
        if (typeof target === 'string') {
            lines.push(`  "${ref}": ${target}`);
            continue;
        }
        const delta = readVec3(fields, 'delta_position');
        const position = readVec3(fields, 'position');
        const rotation = readVec3(fields, 'rotation');
        const scale = readVec3(fields, 'scale');
        const malformed = [delta, position, rotation, scale].find(read => read.error);
        if (malformed) {
            lines.push(`  "${ref}": ${malformed.error}`);
            continue;
        }
        const factor = fields.scale_factor;
        if (factor !== undefined && (typeof factor !== 'number' || !Number.isFinite(factor))) {
            lines.push(`  "${ref}": Error: scale_factor must be a number (got ${JSON.stringify(factor)}). Omit it to leave the scale unchanged.`);
            continue;
        }
        let mate: ReturnType<typeof readMate> | undefined;
        try {
            mate = fields.mate === undefined ? undefined : readMate(fields.mate, r => resolveRef(host, r));
        } catch (error) {
            if (!(error instanceof ObjectRefError)) throw error;
            lines.push(`  "${ref}": ERROR ${error.message} Not moved.`);
            continue;
        }
        if (typeof mate === 'string') {
            lines.push(`  "${ref}": ${mate} Not moved.`);
            continue;
        }
        const seat = readSeat(host, target, fields.seat);
        if (typeof seat === 'string') {
            lines.push(`  "${ref}": ${seat} Not moved.`);
            continue;
        }
        // An absolute value wins over a relative one given beside it.
        const step = position.value ? undefined : delta.value;
        const by = scale.value || factor === undefined || factor === 1 ? undefined : factor;
        const earlier = changed.get(target) ?? { position: false, scale: false };
        if ((step && earlier.position) || (by !== undefined && earlier.scale)) {
            lines.push(`  "${ref}": Error: delta_position and scale_factor apply to the pose before this call, so they cannot follow another update of the same object in it. Combine the updates, or send this one in a later call.`);
            continue;
        }
        const at = position.value ?? (step ? target.transform.position.asArray().map((n, i) => n + step[i]) as Vec3 : undefined);
        const size = scale.value ?? (by !== undefined ? target.transform.scaling.asArray().map(n => n * by) as Vec3 : undefined);
        changed.set(target, { position: earlier.position || !!at || !!mate, scale: earlier.scale || !!size });
        queued.push({ entry: { object: target, position: at, rotation: rotation.value, scale: size, mate }, ref, line: lines.length, seat });
        lines.push('');
    }
    const result = await setTransforms(queued.map(q => q.entry), host.ports());
    const refused = new Map(result.refused.map(refusal => [refusal.entry, refusal]));
    for (const [index, { entry, ref, line }] of queued.entries()) {
        const refusal = refused.get(index);
        lines[line] = !refusal ? `  "${entry.object.name}": updated`
            : refusal.code === 'INVALID_MATE' ? `  "${ref}": ${refusalText(host, refusal)} Not moved.`
            : `  "${entry.object.name}": ERROR ${refusalText(host, refusal)}`;
    }
    const seated = await seatBatch(queued.filter((q, index) => q.seat && !refused.has(index)).map(q => ({ object: q.entry.object, ...q.seat })), host);
    // Where each moved object ended up, after seating, so the model need not measure it again.
    for (const [index, { entry, line }] of queued.entries()) {
        const where = whereText(entry.object);
        if (!refused.has(index)) lines[line] = `  "${entry.object.name}": updated${where ? `.${where}` : ''}`;
    }
    // The moves and the seating are one undo step.
    const commands = [result.command, seated.command].filter((c): c is UndoCommand => !!c);
    keep(host, tool, { ...result, command: commands.length > 1 ? new CompositeCommand(commands[0].label, commands) : commands[0] ?? null });
    return batchOutcome(`${tool}: ${queued.length - result.refused.length}/${updates.length} updated.\n${lines.join('\n')}${seated.text}`, queued.length - result.refused.length);
}

/**
 * Seat the entries that asked, after the batch moved them (`seatParts`, as build_spatial's seat() uses), and
 * make the seating one more transform step: the facts' text, and that step's command. A pass that throws
 * puts every position back and says so; the batch's own moves stand.
 */
async function seatBatch(requests: SeatRequest[], host: SceneToolHost): Promise<{ text: string; command: UndoCommand | null }> {
    if (!requests.length) return { text: '', command: null };
    const from = requests.map(r => r.object.transform.position.asArray());
    const restore = (): void => requests.forEach((r, i) => { r.object.transform.position.fromArray(from[i]); r.object.captureTransform(); });
    try {
        const facts = await seatParts(requests);
        // seatParts writes the positions; put them back and set them as a step of their own, for undo. A part
        // a page's state owns is refused there and stays where the batch put it.
        const to = requests.map(r => r.object.transform.position.asArray());
        restore();
        const moved = requests.map((r, i) => ({ object: r.object, position: to[i] })).filter((e, i) => e.position.some((v, k) => v !== from[i][k]));
        const result = await setTransforms(moved, host.ports());
        // A refused part stays where the batch put it: its line is the refusal, not the move seat measured.
        const refused = new Set(result.refused.map(refusal => moved[refusal.entry].object));
        const failed = result.refused.map(refusal => `! ${moved[refusal.entry].object.name}: not seated: ${refusalText(host, refusal)}`);
        const kept = facts.filter(f => !refused.has(f.object));
        // A part seated onto a refused one was measured against where seat put it, not where it stayed.
        const refusedHolding = (o: SceneObject | null): SceneObject | null => (!o ? null : refused.has(o) ? o : refusedHolding(o.getParent()));
        const stale = kept.flatMap(f => f.contacts.map(c => refusedHolding(c.object)).filter((o): o is SceneObject => !!o)
            .map(o => `! ${f.object.name}: rests on ${o.name}, which was not seated: seat it again once that one is.`));
        return { text: `\n${[...(kept.length ? [seatText(kept)] : []), ...failed, ...stale].join('\n')}`, command: result.command };
    } catch (error) {
        restore();
        return { text: `\nseat: stopped, nothing seated: ${error instanceof Error ? error.message : String(error)}`, command: null };
    }
}

/** A visibility refusal, with the other way out when a page's state owns it: a delete is not routed. */
function activeRefusalText(host: SceneToolHost, refusal: Refusal): string {
    const text = refusalText(host, refusal);
    return refusal.code === 'STATE_ROUTED' && refusal.pageName ? `${text} To remove the object from every page instead, delete_object works whatever page shows.` : text;
}

const SET_ACTIVE: ToolDefinition = {
    name: 'set_active',
    verb: 'Showing or hiding',
    edits: true,
    idempotent: true,
    inputSchema: {
        type: 'object',
        properties: { object: { type: 'string' }, active: { type: 'boolean' }, objects: { type: 'array', items: entryOr(ACTIVE_FIELDS) } },
    },
    async run(input, host) {
        const many = several(input, 'set_active');
        if (typeof many === 'object') return many;
        if (many) return activeMany(input, host);
        const target = resolveRef(host, input.object);
        if (!target) return fail(`Error: Object "${String(input.object)}" not found.`);
        if (typeof input.active !== 'boolean') return fail(`Error: active must be true or false, not ${JSON.stringify(input.active)}.`);
        const result = await setActive([{ object: target, active: input.active }], host.ports());
        const [refused] = result.refused;
        if (refused) return fail(`Error: ${activeRefusalText(host, refused)}`);
        keep(host, 'set_active', result);
        return { text: `Set "${target.name}" active=${input.active}.` };
    },
};
const setActiveTool = defineTool(SET_ACTIVE);

/** set_active on several objects, one value for all or one each, as one step: one line each. */
async function activeMany(input: ToolInput, host: SceneToolHost): Promise<ToolOutcome> {
    const tool = 'set_active';
    const entries = readBatchEntries(input);
    if (typeof entries === 'string') return fail(entries);
    const lines: string[] = [];
    const resolved: { object: SceneObject; active: boolean; line: number }[] = [];
    for (const { object: ref, fields } of entries) {
        const active = fields.active;
        if (typeof active !== 'boolean') {
            lines.push(`  "${ref}": ERROR no boolean "active" (set it per entry or once at the top level)`);
            continue;
        }
        const target = resolveEntry(host, ref);
        if (typeof target === 'string') {
            lines.push(`  "${ref}": ${target}`);
            continue;
        }
        resolved.push({ object: target, active, line: lines.length });
        lines.push('');
    }
    const result = await setActive(resolved, host.ports());
    const refused = new Map(result.refused.map(refusal => [refusal.entry, refusal]));
    for (const [index, { object, active, line }] of resolved.entries()) {
        const refusal = refused.get(index);
        lines[line] = refusal ? `  "${object.name}": ERROR ${activeRefusalText(host, refusal)}` : `  "${object.name}": active=${active}`;
    }
    keep(host, tool, result);
    return batchOutcome(`${tool}: ${resolved.length - result.refused.length}/${entries.length} updated.\n${lines.join('\n')}`, resolved.length - result.refused.length);
}

const SET_COLOR: ToolDefinition = {
    name: 'set_color',
    verb: 'Coloring',
    edits: true,
    idempotent: true,
    inputSchema: { type: 'object', properties: { ...COLOR_FIELDS, objects: { type: 'array', items: entryOr(COLOR_FIELDS) } } },
    async run(input, host) {
        const many = several(input, 'set_color');
        if (typeof many === 'object') return many;
        if (many) return colorMany(input, host);
        const target = resolveRef(host, input.object);
        if (!target) return fail(`Error: Object "${String(input.object)}" not found.`);
        const color = readHexColor(input.color);
        if (!color) return fail(`Error: Invalid color "${String(input.color)}". Use hex format like "#FF0000FF" (RGBA) or "#FF0000" (RGB).`);
        const slot = input.material_index ?? 0;
        const indexError = materialIndexError(slot);
        if (indexError) return fail(`Error: ${indexError}.`);
        let result: Awaited<ReturnType<typeof setMaterialColors>>;
        try {
            result = await setMaterialColors([{ object: target, color, slot: slot as number }], host.ports());
        } catch (error) {
            return fail(`Error setting color: ${error instanceof Error ? error.message : String(error)}`);
        }
        const [refused] = result.refused;
        if (refused) return fail(`Error: ${refusalText(host, refused)}`);
        keep(host, 'set_color', result);
        return { text: `Set color ${color} on "${target.name}"${(slot as number) > 0 ? ` (material ${slot as number})` : ''}.` };
    },
};
const setColorTool = defineTool(SET_COLOR);

/** set_color on several objects, one color for all or one each, as one step: one line each. */
async function colorMany(input: ToolInput, host: SceneToolHost): Promise<ToolOutcome> {
    const tool = 'set_color';
    const entries = readBatchEntries(input);
    if (typeof entries === 'string') return fail(entries);
    const lines: string[] = [];
    const queued: { entry: ColorEntry; line: number }[] = [];
    for (const { object: ref, fields } of entries) {
        const slot = fields.material_index ?? 0;
        if (!fields.color) {
            lines.push(`  "${ref}": ERROR no "color" (set it per entry or once at the top level)`);
            continue;
        }
        const color = readHexColor(fields.color);
        if (!color) {
            lines.push(`  "${ref}": ERROR invalid color ${JSON.stringify(fields.color)} (use hex like "#FF0000" or "#FF0000FF")`);
            continue;
        }
        const indexError = materialIndexError(slot);
        if (indexError) {
            lines.push(`  "${ref}": ERROR ${indexError}`);
            continue;
        }
        const target = resolveEntry(host, ref);
        if (typeof target === 'string') {
            lines.push(`  "${ref}": ${target}`);
            continue;
        }
        queued.push({ entry: { object: target, color, slot: slot as number }, line: lines.length });
        lines.push('');
    }
    let updated = 0;
    try {
        const result = await setMaterialColors(queued.map(q => q.entry), host.ports());
        const refused = new Map(result.refused.map(refusal => [refusal.entry, refusal]));
        for (const [index, { entry, line }] of queued.entries()) {
            const refusal = refused.get(index);
            lines[line] = refusal ? `  "${entry.object.name}": ERROR ${refusalText(host, refusal)}` : `  "${entry.object.name}": ${entry.color}`;
        }
        keep(host, tool, result);
        updated = queued.length - result.refused.length;
    } catch (error) {
        // A fault takes back the whole batch: said once, after the lines.
        for (const { entry, line } of queued) lines[line] = `  "${entry.object.name}": not applied`;
        lines.push(`Error: ${error instanceof Error ? error.message : String(error)}. Nothing in this call was applied.`);
    }
    return batchOutcome(`${tool}: ${updated}/${entries.length} updated.\n${lines.join('\n')}`, updated);
}

const setOpacityTool = defineTool({
    name: 'set_opacity',
    verb: 'Setting opacity on',
    edits: true,
    idempotent: true,
    inputSchema: { type: 'object', properties: { object: { type: 'string' }, opacity: { type: 'number' } }, required: ['object', 'opacity'] },
    async run(input, host) {
        const target = resolveRef(host, input.object);
        if (!target) return fail(`Error: Object "${String(input.object)}" not found.`);
        const opacity = input.opacity;
        if (typeof opacity !== 'number' || !(opacity >= 0 && opacity <= 1)) {
            return fail(`Error: opacity must be a number from 0 (transparent) to 1 (opaque) (got ${JSON.stringify(opacity)}).`);
        }
        const result = await setOpacity([{ object: target, opacity }], host.ports());
        const [refused] = result.refused;
        if (refused) return fail(`Error: ${refusalText(host, refused)}`);
        keep(host, 'set_opacity', result);
        return { text: `Set "${target.name}" opacity=${opacity}.` };
    },
});

export const EDIT_TOOLS: readonly SceneTool[] = [setTransformTool, setActiveTool, setColorTool, setOpacityTool];
